import {
  useReducer, Reducer, Dispatch,
} from 'react';
import { State, Action, ApiResponse } from './types';
import fetchReducer from './fetchReducer';

type UseUpdateResult<TData, TPayload> = [
  State<TData>,
  (payload: TPayload) => Promise<ApiResponse<TData>>,
];

const updateData = async <TData, TPayload>(
  update: (payload: TPayload) => Promise<TData>,
  payload: TPayload,
  dispatch: Dispatch<Action<TData>>,
): Promise<ApiResponse<TData>> => {
  const response: ApiResponse<TData> = {
    data: null,
    error: undefined,
  };
  try {
    dispatch({ type: 'FETCH_INIT' });
    const data = await update(payload);
    dispatch({ type: 'FETCH_SUCCESS', payload: data });
    response.data = data;
  } catch (err) {
    dispatch({ type: 'FETCH_FAILURE', error: err });
    response.error = err;
  }
  return response;
};

const useUpdate = <TData, TPayload = TData>(
  update: (payload: TPayload) => Promise<TData>,
): UseUpdateResult<TData, TPayload> => {
  const initialState = {
    loading: false,
    data: null,
    error: undefined,
  };
  const [state, dispatch] = useReducer<Reducer<State<TData>, Action<TData>>>(
    fetchReducer,
    initialState,
  );

  return [
    state,
    (
      payload: TPayload,
    ): Promise<ApiResponse<TData>> => updateData(update, payload, dispatch),
  ];
};

export default useUpdate;
